import type { Maybe } from 'graphql/jsutils/Maybe';
import React from 'react';

import Image from 'src/components/ui/Image';
import Video from 'src/components/ui/Video';
import EmbedVideo from 'src/components/ui/EmbedVideo';

import type { Medium, EmbedSource } from 'src/types/__generated__/graphql';
import { MediumType } from 'src/types/__generated__/graphql';
import CustomAudio from 'src/components/other/CustomAudio';

export interface MediumProps {
    media: Medium;
}

interface BasicImageMediumProps extends MediumProps {
    alt?: string;
    cropWidth?: number;
    cropHeight?: number;
    objectFit?: 'cover' | 'contain';
}

const BasicImage: React.FC<BasicImageMediumProps> = ({ media, alt, cropWidth, cropHeight, objectFit }) => {
    const width = cropWidth ?? media.width ?? undefined;
    const height =
        cropHeight ?? (cropWidth && media.aspectRatio ? cropWidth * media.aspectRatio : media.height ?? undefined);
    return (
        <Image
            data-target="img-elem"
            width={width}
            height={height}
            src={media.url}
            alt={alt ?? media.url}
            style={objectFit ? { objectFit } : undefined}
        />
    );
};

interface Props {
    media?:
        | Medium
        | Maybe<
              { __typename?: 'Medium' | undefined } & Pick<
                  Medium,
                  | 'type'
                  | 'id'
                  | 'url'
                  | 'height'
                  | 'width'
                  | 'size'
                  | 'aspectRatio'
                  | 'duration'
                  | 'rotation'
                  | 'embedSource'
                  | 'embedId'
                  | 'isTranscoding'
              >
          >
        | undefined;
    alt?: string;
    image?: React.ReactNode | null;
    video?: React.ReactNode | null;
    audio?: React.ReactNode | null;
    embed?: React.ReactNode | null;
    none?: React.ReactNode;
    cropWidth?: number;
    cropHeight?: number;
    objectFit?: 'cover' | 'contain';
}

// will display provided component depending on the media type.
// if no component has been provided, display the basic component version.
// to disallow specific medium type, provide a null value.
const MediumComponent: React.FC<Props> = ({
    media,
    video,
    image,
    audio,
    embed,
    none,
    alt,
    cropWidth,
    cropHeight,
    objectFit,
}) => {
    switch (media?.type) {
        case MediumType.Video:
            return video !== undefined ? (
                (video as React.ReactElement)
            ) : (
                <Video src={media.url} isTranscoding={Boolean(media.isTranscoding)} />
            );
        case MediumType.Audio:
            return audio !== undefined ? (audio as React.ReactElement) : <CustomAudio media={media as Medium} />;
        case MediumType.Image:
            return image !== undefined ? (
                (image as React.ReactElement)
            ) : (
                <BasicImage
                    cropWidth={cropWidth}
                    cropHeight={cropHeight}
                    objectFit={objectFit}
                    media={media as Medium}
                    alt={alt}
                />
            );
        case MediumType.Embed:
            if (embed !== undefined) {
                return embed as React.ReactElement;
            } else {
                return <EmbedVideo embedId={String(media.embedId)} embedSource={media.embedSource as EmbedSource} />;
            }
            return null;
        default:
            return none as React.ReactElement;
    }
};

export default MediumComponent;
